import { AdvancementStatus } from '@appTypes/enums';
import type { CounselorMeritBadge, CounselorYouth } from '@appTypes/esb';

import type { MBVersion, ValidationOK, ValidationResult } from './types';

const COMPLETED_STATUSES: string[] = [
  AdvancementStatus.Awarded,
  AdvancementStatus.LeaderApproved,
  AdvancementStatus.CounselorApproved,
];

function validResult(youth: CounselorYouth): ValidationOK {
  return { valid: true, youth };
}

// TODO: extract into independent validators if validations grow
/**
 * Checks whether a youth is eligible for bulk entry of the selected MB and version.
 *
 * Validation order:
 * 1. Youth must have a counseling entry for the selected MB.
 * 2. Youth must not have already completed the MB (Awarded / Leader Approved / Counselor Approved).
 * 3. If a version is selected, youth must not have started the MB in a different version.
 */
export function validateYouthEligibility(
  youth: CounselorYouth,
  selectedMB: Pick<CounselorMeritBadge, 'meritBadgeId'>,
  selectedVersion?: MBVersion,
): ValidationResult {
  if (!selectedMB) return validResult(youth);

  const counselingEntry = youth.meritBadgesCounseling.find(
    mb => mb.meritBadgeId === selectedMB.meritBadgeId,
  );

  if (!counselingEntry) {
    return {
      youth,
      valid: false,
      reason: 'notCounseled',
      error: 'Youth is not being counseled for this Merit Badge',
    };
  }

  if (COMPLETED_STATUSES.includes(counselingEntry.status ?? '')) {
    return {
      youth,
      valid: false,
      reason: 'completed',
      error: 'Youth already has this Merit Badge completed',
    };
  }

  if (
    selectedVersion &&
    counselingEntry.status === AdvancementStatus.Started &&
    Number(counselingEntry.versionId) !== Number(selectedVersion.versionId)
  ) {
    return {
      youth,
      valid: false,
      reason: 'otherVersion',
      startedVersion: counselingEntry.version ?? undefined,
      error:
        'Youth has already started this Merit Badge in a different version',
    };
  }

  return validResult(youth);
}
